import {ReactElement, useEffect, useState} from "react";
import styled from "styled-components";

import {getContrat} from "@/Api/contrat";
import {DeleteEvenement} from "@/Api/evenements";
import {fetchFournisseurActionUpdateKpi} from "@/Api/fournisseur";
import IconCross from "@/assets/iconCross.svg?react";
import IconRefresh from "@/assets/iconRefresh.svg?react";
import IconUpdate from "@/assets/iconUpdate.svg?react";
import IconValidate from "@/assets/iconValide.svg?react";
import {Button} from "@/components/Button/Button";
import {ErrorMessage} from "@/components/ErrorMessage/ErrorMessage";
import Loader from "@/components/Loader/Loader";
import {ContentModalRecapUpdateKpi} from "@/components/Modal/ContentModalRecapUpdateKpi";
import {ContentModalUpdateKpiValue} from "@/components/Modal/ContentModalUpdateKpiValue";
import {AppContext} from "@/context/AppContext";
import {HookFormContextProvider} from "@/context/HookFormContext";
import {
  UserParamContext,
  UserParamContextType,
} from "@/context/UserParamContext";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {useLoadingState} from "@/hooks/useLoadingState/useLoadingState";
import {useModal} from "@/hooks/useModal/useModale";
import {AppContext as AppContextType} from "@/ts/appContext";
import {Contrat} from "@/ts/contrat";
import {KpiEnCours as KpiEnCoursType} from "@/ts/kpi";

import {KpiEnCours} from "./KpiEnCours";

const TableauKpiEnCoursContent = styled.div<{$isDisabled: boolean}>`
  opacity: ${({$isDisabled}) => ($isDisabled ? 0.6 : 1)};
  cursor: ${({$isDisabled}) => ($isDisabled ? "not-allowed" : "pointer")};
`;

const FournisseurButtonContent = styled.div`
  display: flex;
  height: 100px;
  flex: 1;
  flex-direction: column;
  justify-content: space-around;
  container: FournisseurButtonContent / inline-size;
  & > button {
    width: 135px;
    & > span {
      display: block;
    }
  }
`;

const ValidOrRefuseButtonContent = styled.div`
  display: flex;
  justify-content: space-around;
  & button {
    width: auto;
  }
`;

// on utilise le contrat passé en prop si il est défini, sinon on récupère le contrat du context
export function TableauKpiEnCours({
  contrat: contratFromProps = undefined,
  ...props
}: {
  contrat?: Contrat;
  [key: string]: any;
}): ReactElement {
  // on utilise le contrat passé en prop si il est défini, sinon on récupère le contrat du context
  const [contrat, setContrat] = useState<Contrat | null>(
    contratFromProps || null
  );

  const {currentContremarque} = useGetContext<UserParamContextType>(
    UserParamContext,
    "UserParamContext"
  );
  useEffect(() => {
    setContrat(contratFromProps || null);
  }, [contratFromProps]);

  if (!contrat) {
    return <div>No fournisseur and campagne</div>;
  }

  const kpis = contrat?.campagne?.kpis as KpiEnCoursType[];
  // on récupère l'API et le type d'utilisateur du context
  const {API, userType} = useGetContext<AppContextType>(
    AppContext,
    "AppContext"
  );

  const loadingState = useLoadingState();

  // Gestion du loader pour la modificaiton de KPI
  const fournisseurUpdateKpiLoadingState = useLoadingState();
  const {
    ModalComponent,
    modalSuccessMessage,
    modalErrorMessage,
    modalThemeSecondary,
    openModal,
    updateContent,
    closeModal,
    lockClose,
    unlockClose,
  } = useModal(() => {}, null);

  useEffect(() => {
    if (loadingState.isSuccess) {
      modalSuccessMessage("Modification effectuée avec succès");
      unlockClose();
    }
    if (loadingState.hasError) {
      modalErrorMessage("Erreur lors de la modification");
      unlockClose();
    }
    if (loadingState.isLoading) {
      lockClose();
      updateContent(<Loader isSmall={true} />);
    }
  }, [loadingState.isSuccess, loadingState.hasError, loadingState.isLoading]);

  // On parcourt la liste des KPI pour afficher les éléments correspondants
  // Les éléments sont affichés en fonction du type d'utilisateur connecté
  // Les fonction et bouton sont gérés en fonction du type d'utilisateur connecté
  const KpiList = kpis
    .filter(kpi => kpi.objectif > 0)
    .map(kpiElement => {
      let KpiChildren: ReactElement | null = null;
      // Element a afficher au niveau des KPI si l'adhérent est connecté
      if (userType === "Adherent") {
        if ("en-attente" !== kpiElement?.evenements?.[0]?.statut) {
          const handleClick = (): void => {
            modalThemeSecondary();
            updateContent(
              <HookFormContextProvider>
                <ContentModalUpdateKpiValue
                  contratId={contrat.contrat_id}
                  kpi={kpiElement}
                  closeModal={() => closeModal()}
                  loadingState={loadingState}
                  setContrat={setContrat}
                />
              </HookFormContextProvider>
            );
            openModal();
          };
          KpiChildren = (
            <Button label="Modifier" icon={IconUpdate} onClick={handleClick} />
          );
        } else {
          const handleClick = async (): Promise<void> => {
            await loadingState.executeWithLoading(async () => {
              await DeleteEvenement(
                API,
                kpiElement?.evenements?.[0]?.evenement_id,
                currentContremarque?.value
              );

              const reponse = await getContrat(
                API,
                contrat.contrat_id,
                currentContremarque?.value
              );
              setContrat(reponse as Contrat);
            });
          };
          KpiChildren = loadingState.isLoading ? (
            <Loader isSmall={true} />
          ) : (
            <Button
              label="Annuler"
              variant="negatif_secondary"
              icon={IconCross}
              onClick={handleClick}
            />
          );
        }
      }

      // Element a afficher au niveau des KPI si le fournisseur est connecté
      if (userType === "Fournisseur") {
        const handleAction = async (
          action: "accepter" | "refuser" | "annuler"
        ): Promise<void> => {
          const evenement = kpiElement?.evenements?.[0];
          // On active le bouton si l'événement est en attente et que l'on clique sur accepter ou refuser
          // Ou si l'événement est validé ou refusé et que l'on clique sur annuler
          if (
            ((action === "accepter" || action === "refuser") &&
              evenement?.statut === "en-attente") ||
            (action === "annuler" &&
              (evenement?.statut === "accepte" ||
                evenement?.statut === "refuse"))
          ) {
            const evenementId =
              kpiElement?.evenements?.[0]?.evenement_id?.toString();

            const reponse =
              await fournisseurUpdateKpiLoadingState.executeWithLoading(
                async () => {
                  return await fetchFournisseurActionUpdateKpi(
                    API,
                    evenementId,
                    action
                  );
                }
              );
            if (reponse) {
              setContrat(reponse);
              props?.refreshAllNotifications();
              props?.refreshContrat(reponse);
            }
          }
        };
        if ("en-attente" === kpiElement?.evenements?.[0]?.statut) {
          KpiChildren = (
            <FournisseurButtonContent>
              {fournisseurUpdateKpiLoadingState.isLoading ? (
                <Loader isSmall={true} />
              ) : (
                <>
                  <ValidOrRefuseButtonContent>
                    <Button
                      data-testid="kpi-button-refuser"
                      icon={IconCross}
                      variant="negate"
                      onClick={() => handleAction("refuser")}
                    />
                    <Button
                      data-testid="kpi-button-accepter"
                      icon={IconValidate}
                      variant="validate"
                      onClick={() => handleAction("accepter")}
                    />
                  </ValidOrRefuseButtonContent>
                  <Button
                    label="Consulter"
                    onClick={() => {
                      updateContent(
                        <ContentModalRecapUpdateKpi
                          kpi={kpiElement}
                          handleAction={handleAction}
                          closeModal={closeModal}
                        />
                      );
                      openModal();
                    }}
                  />

                  {/* {kpiElement?.evenements?.[0]?.fichiers ? (
                    <Button label="Fichier" icon={IconDownload} />
                  ) : null}
                  {kpiElement?.evenements?.[0]?.commentaire ? (
                    <Button label="Commentaire" icon={IconSee} />
                  ) : null} */}
                </>
              )}
            </FournisseurButtonContent>
          );
        } else if (kpiElement?.evenements?.[0]?.statut) {
          KpiChildren = fournisseurUpdateKpiLoadingState.isLoading ? (
            <Loader isSmall={true} />
          ) : (
            <FournisseurButtonContent>
              <Button
                data-testid="kpi-button-annuler"
                label={`Annuler ${kpiElement?.evenements?.[0]?.statut === "accepte" ? "validation" : "refus"}`}
                icon={IconRefresh}
                onClick={() => handleAction("annuler")}
              />
            </FournisseurButtonContent>
          );
        }
      }
      return (
        <KpiEnCours kpi={kpiElement as KpiEnCoursType} key={kpiElement.kpi_id}>
          {contrat?.statut === "cloture" ||
          kpiElement?.valeur >= kpiElement?.objectif
            ? null
            : KpiChildren}
        </KpiEnCours>
      );
    });

  return (
    <TableauKpiEnCoursContent $isDisabled={contrat?.statut === "cloture"}>
      {fournisseurUpdateKpiLoadingState.hasError ? (
        <ErrorMessage text={fournisseurUpdateKpiLoadingState.error} />
      ) : null}
      {KpiList}
      {ModalComponent}
    </TableauKpiEnCoursContent>
  );
}
